import { useId } from "react";
import { Controller, useFormContext, type FieldPath, type FieldValues } from "react-hook-form";

import { Label } from "../../atoms/label";
import { cn, platformProps } from "../../lib";
import { Box } from "../../primitives";
import { PhoneNumberField, type PhoneNumberFieldProps } from "../phone-number-field";
import { FormFieldMessage } from "./form-field-message";
import type { FormFieldBaseProps } from "./types";

export type FormPhoneNumberFieldProps<
  TFieldValues extends FieldValues = FieldValues,
  TName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>,
> = FormFieldBaseProps<TFieldValues, TName> &
  Omit<PhoneNumberFieldProps, "state" | "value" | "onChangePhoneNumber" | "onBlur">;

export const FormPhoneNumberField = <
  TFieldValues extends FieldValues = FieldValues,
  TName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>,
>({
  name,
  label,
  required = false,
  helperText,
  hideErrorMessage = false,
  errorMessage,
  containerClassName,
  testID,
  id: idProp,
  ...phoneNumberFieldProps
}: FormPhoneNumberFieldProps<TFieldValues, TName>) => {
  const generatedId = useId();
  const id = idProp ?? generatedId;
  const { control } = useFormContext<TFieldValues>();

  return (
    <Box
      {...platformProps({ slot: "form-phone-number-field", testID })}
      className={cn("gap-spacing-xxs flex flex-col", containerClassName)}
    >
      {label && (
        <Label
          htmlFor={id}
          required={required}
          disabled={phoneNumberFieldProps["disabled"]}
          size={phoneNumberFieldProps.size}
        >
          {label}
        </Label>
      )}
      <Controller
        name={name}
        control={control}
        render={({ field, fieldState }) => {
          const hasError = !!fieldState.error;
          const displayError =
            fieldState.error && !hideErrorMessage
              ? (errorMessage ?? fieldState.error.message)
              : undefined;

          return (
            <>
              <PhoneNumberField
                {...phoneNumberFieldProps}
                ref={field.ref}
                // Native ignores the Label's htmlFor, so the input carries the label
                accessibilityLabel={phoneNumberFieldProps.accessibilityLabel ?? label}
                id={id}
                testID={id}
                state={hasError ? "error" : "default"}
                value={field.value ?? ""}
                onChangePhoneNumber={field.onChange}
                onBlur={field.onBlur}
              />
              <FormFieldMessage error={displayError} helperText={helperText} />
            </>
          );
        }}
      />
    </Box>
  );
};

FormPhoneNumberField.displayName = "FormPhoneNumberField";
